'use client';

import { useState, useEffect } from 'react';
import FAQSearch from './FAQSearch';
import FAQCategory from './FAQCategory';
import FAQItem from './FAQItem';
import QuickContactCard from './QuickContactCard';
import RelatedArticles from './RelatedArticles';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  id: number;
  category: string;
  question: string;
  answer: string;
}

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api';

export default function FAQInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('Semua');
  const [openFAQId, setOpenFAQId] = useState<number | null>(null);
  const [faqData, setFaqData] = useState<FAQ[]>([]);
  const [filteredFAQs, setFilteredFAQs] = useState<FAQ[]>([]);
  const [categories, setCategories] = useState<string[]>(['Semua']);

  useEffect(() => {
    setIsHydrated(true);
    fetchFaqs();
  }, []);

  const fetchFaqs = async () => {
    try {
      const response = await fetch(`${API_URL}/faqs`);
      if (!response.ok) throw new Error('Failed to fetch FAQs');
      const data = await response.json();
      setFaqData(data.faqs);
      setFilteredFAQs(data.faqs);
      setCategories(['Semua', ...data.categories]);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isHydrated || isLoading) return;

    let filtered = faqData;

    if (activeCategory !== 'Semua') {
      filtered = filtered.filter((faq) => faq.category === activeCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (faq) =>
          faq.question.toLowerCase().includes(query) || faq.answer.toLowerCase().includes(query)
      );
    }

    setFilteredFAQs(filtered);
  }, [searchQuery, activeCategory, isHydrated, isLoading, faqData]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    setOpenFAQId(null);
  };

  const handleToggleFAQ = (id: number) => {
    setOpenFAQId(openFAQId === id ? null : id);
  };

  if (!isHydrated || isLoading) {
    return (
      <div id="top" className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-textPrimary mb-4">
              Soalan Lazim (FAQ)
            </h1>
            <p className="text-lg text-textSecondary font-body max-w-2xl mx-auto">
              Cari jawapan kepada soalan-soalan yang kerap ditanya tentang perkhidmatan kami
            </p>
          </div>

          <FAQSearch onSearch={() => {}} totalResults={0} />
          <FAQCategory
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={() => {}}
          />

          <div className="max-w-4xl mx-auto space-y-4 mb-12">
            {/* Loading skeleton */}
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-card rounded-lg border border-border p-6 animate-pulse">
                <div className="h-6 bg-muted rounded w-3/4"></div>
              </div>
            ))}
          </div>

          <QuickContactCard />
          <RelatedArticles />
        </div>
      </div>
    );
  }

  return (
    <div id="top" className="min-h-screen bg-background pt-24 pb-16">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-textPrimary mb-4">
            Soalan Lazim (FAQ)
          </h1>
          <p className="text-lg text-textSecondary font-body max-w-2xl mx-auto">
            Cari jawapan kepada soalan-soalan yang kerap ditanya tentang perkhidmatan kami
          </p>
        </div>

        <FAQSearch onSearch={handleSearch} totalResults={filteredFAQs.length} />
        <FAQCategory
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={handleCategoryChange}
        />

        <div className="max-w-4xl mx-auto space-y-4 mb-12">
          {filteredFAQs.length > 0 ? (
            filteredFAQs.map((faq) => (
              <FAQItem
                key={faq.id}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQId === faq.id}
                onToggle={() => handleToggleFAQ(faq.id)}
              />
            ))
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                <Icon name="MagnifyingGlassIcon" size={32} className="text-textTertiary" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-textPrimary mb-2">
                Tiada Hasil Dijumpai
              </h3>
              <p className="text-textSecondary font-body mb-6">
                Cuba cari dengan kata kunci lain atau hubungi kami terus
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setActiveCategory('Semua');
                }}
                className="px-6 py-2.5 bg-gradient-to-r from-primary to-secondary text-white rounded-lg font-cta font-semibold hover:shadow-brand transition-all duration-300"
              >
                Reset Carian
              </button>
            </div>
          )}
        </div>

        <QuickContactCard />
        <RelatedArticles />
      </div>
    </div>
  );
}
